import { ROUTES } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import passengerTypeStore from 'Models/PassengerTypeStore';
import React from 'react';

import { getStopById } from 'Global/JourneyElements/Utils/getStopById';
import { bookingType, PassengerTypeKey } from 'Models/Enums';
import { store } from 'Models/Store';
import { useStopById } from 'Global/JourneyElements/Hooks/useStopsById';
import { RouteEntity, StopEntity } from 'Models/Entities';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, TableCell, TableRow } from 'semantic-ui-react';
import { getMessageByKey } from 'Stores/LanguageStore';
import { BookingListExportDto, BookingListSummaryDto } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import { formatCurrencyWithNoSymbol } from 'Util/_HumanWritten/CurrencyUtils';
import { formatStandardDate, formatStandardDateTime } from 'Util/_HumanWritten/DateUtils';
import { getTruncatedPrice } from 'Util/_HumanWritten/PriceUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { BookingStatusTag } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import { ExpandableTableCell } from 'Views/Components/_HumanWritten/StaffTables/ExpandableTableCell';
import { Flex } from 'Views/Components/Flex/Flex';
import If from 'Views/Components/If/If';
import GuestTag from 'Views/Components/_HumanWritten/Tag/GuestTag';
import { ColumnConfig, isDynamicFieldColumnKey, dynamicFieldIdFromColumnKey } from './StaffBookingsFilterStore';

export interface BookingRenderProps {
	booking: BookingListExportDto;
	isChecked: boolean;
	onToggleCheckbox: () => void;
	visibleColumns: ColumnConfig[];
}

function getDirectionString(route?: RouteEntity, startStop?: StopEntity, endStop?: StopEntity) {
	let startStopName = route?.departure?.shortName;
	let endStopName = route?.destination?.shortName;

	if (startStop) {
		startStopName = startStop.location?.shortName;
	}

	if (endStop) {
		endStopName = endStop.location?.shortName;
	}

	if (isNullOrUndefined(startStopName) || isNullOrUndefined(endStopName)) {
		return '-';
	}
	return `${startStopName ?? '_'} > ${endStopName ?? '_'}`;
}

function getAmountString(booking: BookingListSummaryDto | BookingListExportDto): string {
	if (booking.status === 'CANCELLED') {
		return '-';
	}

	const totalPrice = getTruncatedPrice(booking.totalCost, true).toFixed(2);
	let paidPrice = getTruncatedPrice(booking.paidPrice, true).toFixed(2);
	if (booking.status === 'INVOICED') {
		paidPrice = totalPrice;
	}

	return `${formatCurrencyWithNoSymbol(parseFloat(paidPrice))}/${formatCurrencyWithNoSymbol(parseFloat(totalPrice))}`;
}

function getTypeString(typeOfBooking: bookingType) {
	// eslint-disable-next-line no-nested-ternary
	if (typeOfBooking === 'PASSENGER_ONLY') {
		return 'PAX';
	}

	if (typeOfBooking === 'EVENT_BOOKING') {
		return 'EVENT';
	}

	if (typeOfBooking === 'FREIGHT') {
		return getMessageByKey('freight__short-name__label') || 'FRT';
	}

	return appConfigStore.vehicleShortName !== '' ? appConfigStore.vehicleShortName : 'VH';
}

export function BookingRender({ booking, isChecked, onToggleCheckbox, visibleColumns }: BookingRenderProps) {
	const navigate = useNavigate();
	const location = useLocation();

	// Whether there is a cancellation reason to show in its column. Absent when the booking is not
	// cancelled, or carries no reason.
	const hasCancellationReason = stringNotEmpty(booking.cancellationReason);
	// On the user detail page's activity tabs the customer cell is plain text — linking to the
	// page you're already on is noise. (The /search-results prefix survives only as a redirect.)
	const isOnSearchResultsPage =
		location.pathname.startsWith('/search-results') || /^\/users\/[^/]+/.test(location.pathname);

	// Use hooks to get stops reactively from the store
	const startStop = useStopById(booking.startStopId ?? '');
	const endStop = useStopById(booking.endStopId ?? '');

	if (isNullOrUndefined(booking)) {
		return null;
	}

	// Helper to check if column is visible
	const isColumnVisible = (key: string) => {
		return visibleColumns.find(col => col.key === key)?.visible ?? true;
	};

	// Staff-UI display mode: show the account holder's company name in place of their personal name
	// when enabled and they have one. Falls back to the personal name otherwise.
	const customerDisplayName =
		appConfigStore.displayCompanyNameInsteadOfName && stringNotEmpty(booking.companyName)
			? booking.companyName!.trim()
			: booking.name;

	const handleNavigation = (e: React.MouseEvent<HTMLTableRowElement>) => {
		// Handle middle click, ctrl+click, cmd+click for new tab
		if (e.button === 1 || e.ctrlKey || e.metaKey) {
			window.open(`/bookings/${booking.id}`, '_blank');
		} else {
			navigate(`/bookings/${booking.id}`);
		}
	};

	const handleContextMenu = (e: React.MouseEvent<HTMLTableRowElement>) => {
		// Allow right-click context menu with proper link behavior
		e.currentTarget.setAttribute('data-href', `/bookings/${booking.id}`);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			navigate(`/bookings/${booking.id}`);
		}
	};

	return (
		<TableRow
			onClick={handleNavigation}
			onAuxClick={handleNavigation} // Handles middle-click
			onContextMenu={handleContextMenu}
			onKeyDown={handleKeyDown}
			className="clickable-row"
			style={{ cursor: 'pointer' }}
			tabIndex={0}
			role="button"
			aria-label={`View booking details for ${booking.name}`}
			data-test-id={`staff-booking-row-${booking.bookingType?.toLowerCase()}`}
		>
			{isColumnVisible('checkbox') && (
				<TableCell
					onClick={(e: any) => {
						e.stopPropagation();
						onToggleCheckbox();
					}}
				>
					<Checkbox checked={isChecked} onChange={onToggleCheckbox} onClick={e => e.stopPropagation()} />
				</TableCell>
			)}
			{isColumnVisible('bookingId') && (
				<TableCell data-test-id="staff-booking-row-id-cell">{booking.humanReadableId}</TableCell>
			)}
			{isColumnVisible('createdDate') && <TableCell>{formatStandardDate(booking.createdDate)}</TableCell>}
			{isColumnVisible('createdBy') && store.isStaff && (
				<TableCell>{stringNotEmpty(booking.createdBy) ? booking.createdBy : '-'}</TableCell>
			)}
			{isColumnVisible('alterationCount') && store.isStaff && (
				<TableCell>{booking.alterationCount ?? 0}</TableCell>
			)}
			{isColumnVisible('customer') && store.isStaff && (
				<TableCell
					onClick={
						isOnSearchResultsPage
							? undefined
							: (e: any) => {
									e.stopPropagation();
								}
					}
				>
					{isOnSearchResultsPage ? (
						<div className="customer-details">
							<div className="customer-details__inner">
								<p>
									<Flex gap="xxs" align="center">
										<span>
											{customerDisplayName}{' '}
											<span className="user-display-name">
												{isNotNullOrUndefined(booking.userDisplayName) &&
													`- ${booking.userDisplayName}`}
											</span>
										</span>
										{booking.isGuest && <GuestTag />}
									</Flex>
								</p>
								<p>{booking.email}</p>
							</div>
						</div>
					) : (
						<Link
							to={ROUTES.USER_DETAIL.url(booking.userId, 'bookings')}
							className="table-cell__container hide-underline"
						>
							<div className="customer-details">
								<div className="customer-details__inner">
									<p>
										<Flex gap="xxs" align="center">
											<span>
												{customerDisplayName}{' '}
												<span className="user-display-name">
													{isNotNullOrUndefined(booking.userDisplayName) &&
														`- ${booking.userDisplayName}`}
												</span>
											</span>
											{booking.isGuest && <GuestTag />}
										</Flex>
									</p>
									<p>{booking.email}</p>
								</div>
							</div>
						</Link>
					)}
				</TableCell>
			)}
			{isColumnVisible('type') && (
				<TableCell className="booking-type__column">{getTypeString(booking.bookingType)}</TableCell>
			)}
			{isColumnVisible('eventName') && appConfigStore.eventsEnabled && <TableCell>{booking.eventName}</TableCell>}
			{isColumnVisible('eventTickets') && appConfigStore.eventsEnabled && (
				<TableCell>{booking.eventTickets}</TableCell>
			)}
			{isColumnVisible('eventPrimaryContact') && appConfigStore.eventsEnabled && (
				<TableCell>
					<If
						condition={
							stringIsEmpty(booking.eventPrimaryName) && stringIsEmpty(booking.eventPrimaryContact)
						}
					>
						<p>-</p>
					</If>
					<If
						condition={
							stringNotEmpty(booking.eventPrimaryName) || stringNotEmpty(booking.eventPrimaryContact)
						}
					>
						<div className="customer-details">
							<div className="customer-details__inner">
								<p>{stringNotEmpty(booking.eventPrimaryName) ? booking.eventPrimaryName : '-'}</p>
								<p>{stringNotEmpty(booking.eventPrimaryContact) ? booking.eventPrimaryContact : '-'}</p>
							</div>
						</div>
					</If>
				</TableCell>
			)}
			{isColumnVisible('direction') && (
				<TableCell>{getDirectionString(booking.route, startStop, endStop)}</TableCell>
			)}
			{isColumnVisible('tripDate') && <TableCell>{formatStandardDateTime(booking.tripDateTime)}</TableCell>}
			{isColumnVisible('vessel') && store.isStaff && <TableCell>{booking.vesselName ?? '-'}</TableCell>}
			{isColumnVisible('vehicleMakeModel') && appConfigStore.vehicleEnabled && (
				<TableCell>{booking.vehicleMakeModel}</TableCell>
			)}
			{isColumnVisible('vehicleLength') && appConfigStore.vehicleEnabled && (
				<TableCell>{booking.vehicleLength}</TableCell>
			)}
			{isColumnVisible('vehicleWeight') && appConfigStore.vehicleEnabled && (
				<TableCell>{booking.vehicleWeight}</TableCell>
			)}
			{isColumnVisible('vehicleWidth') && appConfigStore.vehicleEnabled && appConfigStore.vehicleWidthEnabled && (
				<TableCell>{booking.vehicleWidth}</TableCell>
			)}
			{isColumnVisible('vehicleCharacteristics') && appConfigStore.vehicleEnabled && (
				<ExpandableTableCell
					content={
						stringNotEmpty(booking.vehicleCharacteristics)
							? booking.vehicleCharacteristics?.split(', ')
							: null
					}
					maxItems={2}
				/>
			)}
			{isColumnVisible('rego') && appConfigStore.vehicleEnabled && <TableCell>{booking.rego}</TableCell>}
			{isColumnVisible('driverContact') && appConfigStore.vehicleEnabled && (
				<TableCell>
					<If condition={stringIsEmpty(booking.driverName) && stringIsEmpty(booking.driverPhone)}>
						<p>-</p>
					</If>
					<If condition={stringNotEmpty(booking.driverName) || stringNotEmpty(booking.driverPhone)}>
						<div className="customer-details">
							<div className="customer-details__inner">
								<p>{stringNotEmpty(booking.driverName) ? booking.driverName : '-'}</p>
								<p>{stringNotEmpty(booking.driverPhone) ? booking.driverPhone : '-'}</p>
							</div>
						</div>
					</If>
				</TableCell>
			)}
			{isColumnVisible('trailerType') && appConfigStore.trailersEnabled && (
				<TableCell>{booking.trailerType}</TableCell>
			)}
			{isColumnVisible('trailerLength') && appConfigStore.trailersEnabled && (
				<TableCell>{booking.trailerLength}</TableCell>
			)}
			{isColumnVisible('trailerWeight') && appConfigStore.trailersEnabled && (
				<TableCell>{booking.trailerWeight}</TableCell>
			)}
			{isColumnVisible('trailerWidth') &&
				appConfigStore.trailersEnabled &&
				appConfigStore.vehicleWidthEnabled && <TableCell>{booking.trailerWidth}</TableCell>}
			{isColumnVisible('trailerCharacteristics') && appConfigStore.trailersEnabled && (
				<ExpandableTableCell
					content={
						stringNotEmpty(booking.trailerCharacteristics)
							? booking.trailerCharacteristics?.split(', ')
							: null
					}
					maxItems={2}
				/>
			)}
			{isColumnVisible('passengers') && (
				<TableCell>
					{booking.passengerCounts && Object.keys(booking.passengerCounts).length > 0
						? Object.entries(booking.passengerCounts)
								.filter(([_, count]) => count > 0)
								.map(
									([key, count]) =>
										`${count} ${passengerTypeStore.getAbbreviation(key as PassengerTypeKey)}`,
								)
								.join(', ')
						: '-'}
				</TableCell>
			)}
			{isColumnVisible('passengerNames') && appConfigStore.passengerInfoEnabled && (
				<ExpandableTableCell
					content={stringNotEmpty(booking.passengerNames) ? booking.passengerNames?.split(', ') : null}
					maxItems={2}
				/>
			)}
			{isColumnVisible('passengerClasses') && appConfigStore.passengerClassesEnabled && (
				<TableCell>
					{stringNotEmpty(booking.passengerClasses) ? booking.passengerClasses : '-'}
				</TableCell>
			)}
			{/*
				Expandable like the names beside it: a party of eight across two decks is a long
				line, and the seats are the kind of thing you want to open rather than scan past.
			*/}
			{isColumnVisible('seats') && appConfigStore.passengerClassesEnabled && (
				<ExpandableTableCell
					content={stringNotEmpty(booking.seats) ? booking.seats?.split(', ') : null}
					maxItems={2}
				/>
			)}
			{isColumnVisible('additionalInformation') &&
				(appConfigStore.passengerNoteEnabled || appConfigStore.vehicleNoteEnabled) && (
					<TableCell>
						{stringNotEmpty(booking.additionalInformation) ? booking.additionalInformation : '-'}
					</TableCell>
				)}
			{/* One cell per visible dynamic-field column, in the same order as the
			    headers built in StaffBookingsCollection. */}
			{visibleColumns
				.filter(col => isDynamicFieldColumnKey(col.key) && col.visible)
				.map(col => {
					const value = booking.dynamicFieldValues?.[dynamicFieldIdFromColumnKey(col.key)];
					return <TableCell key={col.key}>{stringNotEmpty(value) ? value : '-'}</TableCell>;
				})}
			{isColumnVisible('freightDescription') && appConfigStore.freightEnabled && (
				<TableCell>{stringNotEmpty(booking.freightDescription) ? booking.freightDescription : '-'}</TableCell>
			)}
			{isColumnVisible('freightDimensions') && appConfigStore.freightEnabled && (
				<TableCell>{stringNotEmpty(booking.freightDimensions) ? booking.freightDimensions : '-'}</TableCell>
			)}
			{isColumnVisible('freightReceiverDetails') &&
				appConfigStore.freightEnabled &&
				appConfigStore.freightReceiverDetailsEnabled && (
					<TableCell>
						<If
							condition={
								stringIsEmpty(booking.freightReceiverName) &&
								stringIsEmpty(booking.freightReceiverContact)
							}
						>
							<p>-</p>
						</If>
						<If
							condition={
								stringNotEmpty(booking.freightReceiverName) ||
								stringNotEmpty(booking.freightReceiverContact)
							}
						>
							<div className="customer-details">
								<div className="customer-details__inner">
									<p>
										{stringNotEmpty(booking.freightReceiverName)
											? booking.freightReceiverName
											: '-'}
									</p>
									<p>
										{stringNotEmpty(booking.freightReceiverContact)
											? booking.freightReceiverContact
											: '-'}
									</p>
								</div>
							</div>
						</If>
					</TableCell>
				)}
			{isColumnVisible('freightNotifyDetails') &&
				appConfigStore.freightEnabled &&
				appConfigStore.freightNotifyDetailsEnabled && (
					<TableCell>
						<If
							condition={
								stringIsEmpty(booking.freightNotifyName) && stringIsEmpty(booking.freightNotifyContact)
							}
						>
							<p>-</p>
						</If>
						<If
							condition={
								stringNotEmpty(booking.freightNotifyName) ||
								stringNotEmpty(booking.freightNotifyContact)
							}
						>
							<div className="customer-details">
								<div className="customer-details__inner">
									<p>{stringNotEmpty(booking.freightNotifyName) ? booking.freightNotifyName : '-'}</p>
									<p>
										{stringNotEmpty(booking.freightNotifyContact)
											? booking.freightNotifyContact
											: '-'}
									</p>
								</div>
							</div>
						</If>
					</TableCell>
				)}
			{isColumnVisible('addOns') && (
				<ExpandableTableCell
					content={stringNotEmpty(booking.addOns) ? booking.addOns?.split(', ') : null}
					maxItems={2}
					fallback="-"
				/>
			)}
			{isColumnVisible('amount') && <TableCell>{getAmountString(booking)}</TableCell>}
			{isColumnVisible('travelStatus') && <TableCell>{booking.travelStatus}</TableCell>}
			{isColumnVisible('status') && (
				<TableCell className="staff-bookings__status-cell">
					<div className="staff-bookings__status-tags">
						{(booking.transactionStatuses?.length ? booking.transactionStatuses : [booking.status]).map(
							status => (
								<BookingStatusTag key={status} status={status} isStaff={store.isStaff} />
							),
						)}
					</div>
				</TableCell>
			)}
			{isColumnVisible('cancellationReason') && appConfigStore.cancellationReasonsEnabled && (
				<TableCell>
					{hasCancellationReason ? (
						<span>
							{booking.cancellationReason}
							{stringNotEmpty(booking.cancellationReasonDetail) && (
								<span className="text-gray-500"> — {booking.cancellationReasonDetail}</span>
							)}
						</span>
					) : (
						'-'
					)}
				</TableCell>
			)}
			{isColumnVisible('legacyId') && appConfigStore.systemWasMigrated && (
				<TableCell>{booking.legacyId ?? '-'}</TableCell>
			)}
		</TableRow>
	);
}
